import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useExpenses, useProfile } from "../hooks/useStudyOS";
import { deleteExpense, updateBudget } from "../lib/db";

export default function FinanceScreen() {
  const { openModal } = useOutletContext();
  const { expenses, totalSpent, spentByCategory } = useExpenses();
  const { profile } = useProfile();
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetMode, setBudgetMode] = useState("edit"); // "edit" | "reset"
  const [budgetForm, setBudgetForm] = useState({});
  const [saving, setSaving] = useState(false);

  const budget = profile?.budget || { total: 0, categories: [] };
  const remaining = budget.total - totalSpent;
  const overBudget = budget.total > 0 && totalSpent > budget.total;

  const DEFAULT_CATS = [
    "Food", "Transport", "Data & airtime",
    "Books & printing", "Entertainment", "Miscellaneous"
  ];

  const getBarColor = (pct) => {
    if (pct >= 90) return "var(--red)";
    if (pct >= 70) return "var(--amber)";
    return "var(--green)";
  };

  const openEditBudget = () => {
    setBudgetMode("edit");
    const existing = {};
    budget.categories.forEach(c => { existing[c.name.replace(/\s/g,"_")] = c.amount; });
    setBudgetForm({ total: budget.total || "", ...existing });
    setShowBudgetModal(true);
  };

  const openResetBudget = () => {
    setBudgetMode("reset");
    setBudgetForm({});
    setShowBudgetModal(true);
  };

  const handleSaveBudget = async () => {
    if (!budgetForm.total || parseFloat(budgetForm.total) <= 0) return;
    setSaving(true);
    const categories = DEFAULT_CATS.map(name => ({
      name,
      amount: parseFloat(budgetForm[name.replace(/\s/g,"_")] || 0)
    })).filter(c => c.amount > 0);

    await updateBudget({
      total: parseFloat(budgetForm.total),
      categories,
    });
    setShowBudgetModal(false);
    setBudgetForm({});
    setSaving(false);
  };

  const handleResetBudget = async () => {
    if (!window.confirm("Reset your entire budget? This will clear all category allocations but keep your expense history.")) return;
    setSaving(true);
    await updateBudget({ total: 0, categories: [] });
    setShowBudgetModal(false);
    setSaving(false);
  };

  return (
    <div className="screen-wrap">

      {/* Budget not set */}
      {!budget.total && (
        <div style={styles.setupBanner}>
          <span style={{fontSize:"18px"}}>💡</span>
          <span style={styles.setupText}>Set your monthly budget to track spending</span>
          <button style={styles.setupBtn} onClick={openEditBudget}>Set up</button>
        </div>
      )}

      {/* Budget header */}
      <div style={styles.budgetRow}>
        <div style={styles.budgetCard}>
          <div style={styles.budgetLabel}>Monthly budget</div>
          <div style={styles.budgetAmount}>
            {budget.total > 0 ? `GHS ${budget.total.toLocaleString()}` : "Not set"}
          </div>
        </div>
        <div style={{...styles.budgetCard, background:"var(--surface)", border:"1px solid var(--border)"}}>
          <div style={{...styles.budgetLabel, color:"var(--text2)"}}>Spent so far</div>
          <div style={{...styles.budgetAmount, color: overBudget ? "var(--red)" : "var(--text)"}}>
            GHS {totalSpent.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Remaining bar */}
      {budget.total > 0 && (
        <>
          <div style={styles.remainRow}>
            <span style={styles.remainLabel}>Remaining this month</span>
            <span style={{...styles.remainAmt, color: remaining < 0 ? "var(--red)" : "var(--green)"}}>
              GHS {remaining.toLocaleString()}
            </span>
          </div>
          <div style={styles.totalBar}>
            <div style={{
              ...styles.totalFill,
              width:`${Math.min(100, Math.round((totalSpent/budget.total)*100))}%`,
              background: getBarColor(Math.round((totalSpent/budget.total)*100))
            }}/>
          </div>
        </>
      )}

      {/* Budget actions */}
      {budget.total > 0 && (
        <div style={styles.budgetActions}>
          <button style={styles.actionBtn} onClick={openEditBudget}>
            ✏️ Edit budget
          </button>
          <button style={styles.actionBtn} onClick={openResetBudget}>
            🔄 Add funds
          </button>
          <button style={{...styles.actionBtn, color:"var(--red)", borderColor:"var(--red-bg)"}}
            onClick={async () => {
              if (window.confirm("Reset entire budget? This clears all allocations.")) {
                await updateBudget({ total: 0, categories: [] });
              }
            }}>
            🗑 Reset all
          </button>
        </div>
      )}

      {/* Categories */}
      <div style={styles.sLabel}>By category</div>
      {budget.categories.length === 0 ? (
        <div style={styles.card}>
          <div style={styles.emptyText}>No budget categories set.</div>
        </div>
      ) : (
        <div style={styles.card}>
          {budget.categories.map(cat => {
            const spent = spentByCategory[cat.name] || 0;
            const pct = cat.amount > 0 ? Math.min(100, Math.round((spent/cat.amount)*100)) : 0;
            return (
              <div key={cat.name} style={styles.catItem}>
                <div style={styles.catHeader}>
                  <span style={styles.catName}>{cat.name}</span>
                  <span style={styles.catAmt}>GHS {spent} / {cat.amount}</span>
                </div>
                <div style={styles.barTrack}>
                  <div style={{...styles.barFill, width:`${pct}%`, background: getBarColor(pct)}}/>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent expenses */}
      <div style={styles.rowHeader}>
        <div style={styles.sLabel}>Recent expenses</div>
      </div>
      {expenses.length === 0 ? (
        <div style={styles.card}>
          <div style={styles.emptyText}>No expenses logged yet. Tap + to add one.</div>
        </div>
      ) : (
        <div style={styles.card}>
          {expenses.slice(0,15).map((e, i, arr) => (
            <div key={e.id} style={{...styles.expRow, borderBottom: i < arr.length-1 ? "1px solid var(--border)" : "none"}}>
              <div style={{...styles.expIcon, background: e.color}}>{e.icon}</div>
              <div style={styles.expInfo}>
                <div style={styles.expName}>{e.name}</div>
                <div style={styles.expMeta}>{e.category} · {e.createdAt?.toDate?.()?.toLocaleDateString() || ""}</div>
              </div>
              <div style={styles.expRight}>
                <div style={styles.expAmount}>GHS {e.amount}</div>
                <button style={styles.delBtn} onClick={() => deleteExpense(e.id)}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB for adding expense */}
      <div style={{height:"20px"}}/>

      {/* Budget Modal */}
      {showBudgetModal && (
        <div style={styles.overlay} onClick={e => e.target===e.currentTarget && setShowBudgetModal(false)}>
          <div style={styles.modalBox}>
            <div style={styles.handle}/>
            <div style={styles.modalTitle}>
              {budgetMode === "edit" ? "✏️ Edit monthly budget" : "🔄 Add funds to budget"}
            </div>

            {budgetMode === "add" && (
              <div style={{fontSize:"13px", color:"var(--text2)", marginBottom:"16px", lineHeight:1.6}}>
                Received extra money? Update your total budget and reallocate categories.
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>
                {budgetMode === "edit" ? "Total monthly budget (GHS)" : "New total budget (GHS)"}
              </label>
              <input style={styles.input} type="number" placeholder="e.g. 800"
                value={budgetForm.total||""}
                onChange={e => setBudgetForm({...budgetForm, total: e.target.value})}/>
            </div>

            <label style={{...styles.label, display:"block", marginBottom:"10px"}}>
              Allocate by category (GHS)
            </label>

            {DEFAULT_CATS.map(cat => (
              <div key={cat} style={{display:"flex", alignItems:"center", gap:"10px", marginBottom:"10px"}}>
                <span style={{flex:1, fontSize:"13px", color:"var(--text2)"}}>{cat}</span>
                <input
                  style={{...styles.input, width:"100px", padding:"8px", textAlign:"right"}}
                  type="number" placeholder="0"
                  value={budgetForm[cat.replace(/\s/g,"_")]||""}
                  onChange={e => setBudgetForm({...budgetForm, [cat.replace(/\s/g,"_")]: e.target.value})}/>
              </div>
            ))}

            <button style={styles.submitBtn} onClick={handleSaveBudget} disabled={saving}>
              {saving ? "Saving..." : "Save budget"}
            </button>
            <button style={{...styles.submitBtn, background:"transparent", border:"1px solid var(--border2)", color:"var(--text2)", marginTop:"8px"}}
              onClick={() => setShowBudgetModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  setupBanner:  { background:"var(--amber-bg)", border:"1px solid rgba(240,164,41,.2)", borderRadius:"16px", padding:"14px 16px", marginBottom:"16px", display:"flex", alignItems:"center", gap:"12px" },
  setupText:    { flex:1, fontSize:"13px", color:"var(--amber)", fontWeight:500 },
  setupBtn:     { fontSize:"12px", fontWeight:600, color:"var(--amber)", background:"var(--amber-bg)", border:"none", borderRadius:"8px", padding:"5px 10px", cursor:"pointer", fontFamily:"inherit" },
  budgetRow:    { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginBottom:"12px" },
  budgetCard:   { background:"var(--accent-bg)", border:"1px solid var(--accent-border)", borderRadius:"16px", padding:"16px" },
  budgetLabel:  { fontSize:"12px", color:"var(--accent2)", fontWeight:500 },
  budgetAmount: { fontSize:"26px", fontWeight:700, color:"var(--accent2)", letterSpacing:"-.5px", marginTop:"2px" },
  remainRow:    { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"8px" },
  remainLabel:  { fontSize:"13px", color:"var(--text2)" },
  remainAmt:    { fontSize:"14px", fontWeight:700 },
  totalBar:     { height:"6px", background:"var(--surface2)", borderRadius:"3px", overflow:"hidden", marginBottom:"16px" },
  totalFill:    { height:"100%", borderRadius:"3px", transition:"width .5s ease" },
  budgetActions:{ display:"flex", gap:"8px", marginBottom:"20px", flexWrap:"wrap" },
  actionBtn:    { padding:"8px 14px", background:"var(--surface)", border:"1px solid var(--border2)", borderRadius:"10px", fontFamily:"inherit", fontSize:"13px", fontWeight:500, color:"var(--text2)", cursor:"pointer" },
  sLabel:       { fontSize:"11px", fontWeight:600, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".08em", margin:"0 0 10px" },
  card:         { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"16px", padding:"4px 16px", marginBottom:"16px" },
  catItem:      { padding:"12px 0", borderBottom:"1px solid var(--border)" },
  catHeader:    { display:"flex", justifyContent:"space-between", marginBottom:"6px" },
  catName:      { fontSize:"13px", fontWeight:500, color:"var(--text)" },
  catAmt:       { fontSize:"12px", color:"var(--text2)" },
  barTrack:     { height:"6px", background:"var(--surface2)", borderRadius:"3px", overflow:"hidden" },
  barFill:      { height:"100%", borderRadius:"3px", transition:"width .5s ease" },
  rowHeader:    { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"10px", marginTop:"4px" },
  expRow:       { display:"flex", alignItems:"center", gap:"12px", padding:"11px 0" },
  expIcon:      { width:"38px", height:"38px", borderRadius:"10px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"17px", flexShrink:0 },
  expInfo:      { flex:1, minWidth:0 },
  expName:      { fontSize:"13px", fontWeight:500, color:"var(--text)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  expMeta:      { fontSize:"11px", color:"var(--text3)", marginTop:"2px" },
  expRight:     { display:"flex", alignItems:"center", gap:"6px" },
  expAmount:    { fontSize:"14px", fontWeight:700, color:"var(--text)" },
  delBtn:       { background:"none", border:"none", color:"var(--text3)", fontSize:"18px", cursor:"pointer", padding:"0 4px" },
  emptyText:    { fontSize:"13px", color:"var(--text3)", padding:"12px 0", textAlign:"center" },
  overlay:      { position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:200 },
  modalBox:     { background:"var(--modal-bg)", borderRadius:"24px 24px 0 0", padding:"20px 20px 40px", width:"100%", maxWidth:"480px", maxHeight:"90vh", overflowY:"auto" },
  handle:       { width:"38px", height:"4px", background:"var(--surface2)", borderRadius:"2px", margin:"0 auto 18px" },
  modalTitle:   { fontSize:"18px", fontWeight:700, color:"var(--text)", marginBottom:"18px" },
  formGroup:    { marginBottom:"14px" },
  label:        { fontSize:"12px", fontWeight:600, color:"var(--text2)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:"6px", display:"block" },
  input:        { width:"100%", padding:"12px 14px", background:"var(--input-bg)", border:"1px solid var(--border2)", borderRadius:"8px", fontFamily:"inherit", fontSize:"14px", color:"var(--text)", outline:"none" },
  submitBtn:    { width:"100%", padding:"14px", background:"var(--accent)", border:"none", borderRadius:"12px", fontFamily:"inherit", fontSize:"15px", fontWeight:600, color:"#fff", cursor:"pointer", marginTop:"4px" },
};